import { useState, useEffect, useMemo, useRef } from "react";
import {
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "../../Vocabularies/Crud/ResourceDetail.css";
import { buildServiceUrl } from "../../../utils/urlBuilder";
import axios from "axios";
import "./ActivateUserForm.css";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useAuthRoles } from "../../../hooks/useAuthRoles";
import { useWorkerApiHelper } from "../../../hooks/useWorkerApiHelper";
import { useGetParticipants } from "../../../hooks/participants/useGetParticipants";
import { usePutInternalUser } from "../../../hooks/users/usePutInternalUser";
import { usePutUserRealmRoles } from "../../../hooks/users/usePutUserRealmRoles";
import { getParticipantDisplayName } from "../../../utils/participants";

export default function ActivateUserForm({
  userInfo,
  closePopover,
  onUpdateTable,
  setTableData,
}) {
  const { t, i18n } = useTranslation();
  const [rolesSelected, setRolesSelected] = useState([]);
  const [participantIdSelected, setParticipantIdSelected] = useState("");
  const [organizationValue, setOrganizationValue] = useState("");
  const participantFieldRef = useRef(null);
  const { token } = useAuthRoles();
  const { callApiWithWorker } = useWorkerApiHelper();
  const { mutateAsync: updateInternalUser } = usePutInternalUser();
  const { mutateAsync: updateUserRealmRoles } = usePutUserRealmRoles();
  const [loading, setLoading] = useState(false);
  const [participantMenuWidth, setParticipantMenuWidth] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const { data: participantsResponse, isLoading: isParticipantsLoading } =
    useGetParticipants({
      callApiWithWorker,
      lang: i18n.language,
      pageIndex: 1,
      pageSize: 1000,
    });

  useEffect(() => {
    if (userInfo?.attributes) {
      const preselected =
        userInfo.attributes["userAttributes.roles"] ||
        userInfo.attributes.userAttributes?.roles ||
        [];
      const preselectedParticipantId =
        userInfo.attributes?.participant_id?.[0] ||
        userInfo.attributes?.participant_id ||
        "";
      const preselectedOrganization =
        userInfo.attributes?.organization?.[0] ||
        userInfo.attributes?.organization ||
        "";

      const selectedRole = Array.isArray(preselected)
        ? preselected[0]
        : preselected;
      setRolesSelected(selectedRole ? [selectedRole] : []);
      setParticipantIdSelected(preselectedParticipantId);
      setOrganizationValue(preselectedOrganization);
    }
  }, [userInfo]);

  const roles = [
    { value: "data_provider", label: t("roles.data_provider") },
    { value: "data_consumer", label: t("roles.data_consumer") },
  ];

  const participantOptions = useMemo(() => {
    const content = participantsResponse?.content ?? [];

    return content
      .map((participant) => {
        const participantId = participant?.dsbParticipantId ?? "";
        const participantName =
          getParticipantDisplayName(participant, i18n.language) ||
          participant?.legal_name ||
          participantId;

        return {
          value: participantId,
          label: `${participantName}`,
          organization: participantName,
        };
      })
      .filter((participant) => participant.value)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [i18n.language, participantsResponse]);

  const participantLookup = useMemo(
    () =>
      participantOptions.reduce((acc, participant) => {
        acc[participant.value] = participant;
        return acc;
      }, {}),
    [participantOptions],
  );

  const participantRawLookup = useMemo(() => {
    const content = participantsResponse?.content ?? [];
    return content.reduce((acc, participant) => {
      const participantId =
        participant?.dsbParticipantId ??
        participant?.identifier?.[0]?.notation ??
        participant?.id ??
        "";
      if (participantId) {
        acc[participantId] = participant;
      }
      return acc;
    }, {});
  }, [participantsResponse]);

  useEffect(() => {
    const updateParticipantMenuWidth = () => {
      setParticipantMenuWidth(participantFieldRef.current?.offsetWidth ?? null);
    };

    updateParticipantMenuWidth();
    window.addEventListener("resize", updateParticipantMenuWidth);

    return () => {
      window.removeEventListener("resize", updateParticipantMenuWidth);
    };
  }, []);

  const handleConfirm = async () => {
    if (rolesSelected.length !== 1 || !participantIdSelected) return;
    setLoading(true);

    try {
      const selectedParticipant = participantLookup[participantIdSelected];
      const selectedParticipantRaw =
        participantRawLookup[participantIdSelected];
      const preferredLabelValue =
        selectedParticipantRaw?.preferredLabel?.[0]?.value ||
        selectedParticipantRaw?.preferredLabel?.[0]?.text ||
        "";
      const organization =
        preferredLabelValue ||
        organizationValue ||
        selectedParticipant?.organization ||
        userInfo.attributes?.organization?.[0] ||
        userInfo.attributes?.organization ||
        "";

      const updatedUser = {
        username: userInfo.username,
        email: userInfo.email,
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        enabled: true,
        attributes: {
          organization: organization,
          participant_id: participantIdSelected,
        },
      };

      await updateInternalUser({
        userId: userInfo.id,
        payload: updatedUser,
      });

      await updateUserRealmRoles({
        userId: userInfo.id,
        roleNames: rolesSelected,
      });

      toast.success(t("toaster.userApproved"));
      closePopover();
      if (onUpdateTable && setTableData) {
        const updated = await onUpdateTable();
        setTableData(updated);
      }
    } catch (err) {
      toast.error(t("toaster.userError"));
    } finally {
      setLoading(false);
    }
  };

  const handleRefuse = async () => {
    if (!userInfo?.id) return;
    setLoading(true);

    try {
      const { data: user } = await axios.get(
        `${buildServiceUrl(
          "keycloak",
          `admin/realms/DSB/users/${userInfo.id}`,
        )}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      const firstName = normalize(user.firstName);
      const lastName = normalize(user.lastName);
      const organization = normalize(user.attributes?.organization?.[0] || "");

      const userRoles =
        userInfo.attributes?.["userAttributes.roles"] ||
        userInfo.attributes?.userAttributes?.roles ||
        [];

      const rolePrefixMap = {
        data_provider: "dp",
        data_consumer: "dc",
        vocabulary_provider: "vp",
      };

      for (const role of userRoles) {
        const prefix = rolePrefixMap[role];
        if (!prefix) continue;

        const clientId = `${prefix}_${firstName}_${lastName}_${organization}`;

        try {
          const { data: clients } = await axios.get(
            `${buildServiceUrl("keycloak", `admin/realms/DSB/clients`)}`,
            {
              headers: { Authorization: `Bearer ${token}` },
              params: { clientId },
            },
          );

          if (clients?.length) {
            const clientUuid = clients[0].id;
            await axios.delete(
              `${buildServiceUrl(
                "keycloak",
                `admin/realms/DSB/clients/${clientUuid}`,
              )}`,
              { headers: { Authorization: `Bearer ${token}` } },
            );
          } else {
          }
        } catch (err) {
          console.error(`Error deleting client ${clientId}:`, err);
        }
      }

      await axios.delete(
        `${buildServiceUrl(
          "keycloak",
          `admin/realms/DSB/users/${userInfo.id}`,
        )}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      toast.success(t("toaster.userRefused"));
      closePopover();

      if (onUpdateTable && setTableData) {
        const updated = await onUpdateTable();
        setTableData(updated);
      }
    } catch (err) {
      console.error("Error deleting user:", err);
      toast.error(t("toaster.userError"));
    } finally {
      setLoading(false);
    }
  };
  const normalize = (str) =>
    str ? str.trim().toLowerCase().replace(/\s+/g, "_") : "";

  const handleCancel = () => closePopover();

  if (!userInfo) return null;

  return (
    <div className="activate-user-form">
      <div className="activate-header">
        <Typography variant="h6" className="activate-title">
          {userInfo.firstName} {userInfo.lastName}
        </Typography>
        <IconButton aria-label="close" onClick={handleCancel} size="small">
          <CloseIcon />
        </IconButton>
      </div>

      <div className="activate-body">
        <div className="form-group">
          <p className="ptitleinput">{t("register.organization")}</p>
          <input
            className="inputofferdisabled mb4"
            type="text"
            value={organizationValue}
            disabled
          />
        </div>
        <FormControl
          ref={participantFieldRef}
          fullWidth
          className="activate-select"
        >
          <InputLabel id="activate-user-participant-label">
            {t("createuser.fields.participant")}
          </InputLabel>
          <Select
            labelId="activate-user-participant-label"
            value={participantIdSelected}
            label={t("createuser.fields.participant")}
            onChange={(e) => {
              const selectedId = e.target.value;
              const selectedParticipantRaw = participantRawLookup[selectedId];
              const selectedPreferredLabel =
                selectedParticipantRaw?.preferredLabel?.[0]?.value ||
                selectedParticipantRaw?.preferredLabel?.[0]?.text ||
                "";

              setParticipantIdSelected(selectedId);
              if (selectedPreferredLabel) {
                setOrganizationValue(selectedPreferredLabel);
              }
            }}
            disabled={isParticipantsLoading}
            MenuProps={{
              PaperProps: {
                sx: {
                  width: participantMenuWidth ?? undefined,
                  maxWidth: participantMenuWidth ?? undefined,
                  maxHeight: 48 * 6,
                },
              },
            }}
          >
            {participantOptions.map((participant) => (
              <MenuItem
                key={participant.value}
                value={participant.value}
                sx={{
                  whiteSpace: "normal",
                  overflowWrap: "anywhere",
                }}
              >
                {participant.label}
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            {isParticipantsLoading ? (
              <span className="create-user-inline-loading">
                <CircularProgress size={14} />
                {t("createuser.loadingParticipants")}
              </span>
            ) : (
              t("createuser.participantHint")
            )}
          </Typography>
        </FormControl>
        <Select
          value={rolesSelected[0] ?? ""}
          onChange={(e) => setRolesSelected([e.target.value])}
          displayEmpty
          fullWidth
          className="activate-select"
          renderValue={(selected) =>
            roles.find((r) => r.value === selected)?.label ||
            t("profilePage.selectRole")
          }
        >
          {roles.map((r) => (
            <MenuItem key={r.value} value={r.value}>
              {r.label}
            </MenuItem>
          ))}
        </Select>
      </div>

      <div className="activate-footer">
        <Button
          variant="outlined"
          onClick={handleRefuse}
          disabled={loading}
          sx={{
            color: "var(--main-color)",
            borderColor: "var(--main-color)",
            backgroundColor: "#ffffff",
            "&:hover": {
              backgroundColor: "#ffffff",
              borderColor: "var(--main-color)",
            },
          }}
        >
          {t("refuse")}
        </Button>
        <Button
          variant="contained"
          sx={{
            backgroundColor: "var(--main-color)",
            "&:hover": {
              backgroundColor: "var(--main-color)",
            },
          }}
          onClick={handleConfirm}
          disabled={
            rolesSelected.length !== 1 || !participantIdSelected || loading
          }
        >
          {loading ? "Procesando..." : t("profilePage.approve")}
        </Button>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
